import React from 'react';
import { motion } from 'motion/react';
import {
  Shield,
  Bell,
  MapPin,
  Users,
  AlertTriangle,
  Phone,
  Home,
  GraduationCap,
  Building2,
  Radio,
  FileText,
  Eye,
  UserCheck,
  Footprints,
  Map,
  Siren,
  Clock,
  CheckCircle,
  ArrowRight,
  Check,
  Heart,
  Smartphone,
  HandHelping,
  Facebook,
  Instagram,
  Linkedin,
  Globe,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PDFDownloadButton from '../components/PDFDownloadButton';
import '../styles/swiftguard.css';

const userCategories = [
  {
    title: 'For Individuals & Families',
    icon: Users,
    color: 'red',
    features: [
      { name: 'SOS Button', desc: 'Get urgent help for medical emergencies, accidents, crime, or disasters' },
      { name: 'Public Health Alerts', desc: 'Receive trusted updates from national agencies and local authorities' },
      { name: 'Nearby Help Anytime', desc: 'Whether at home, walking alone, or traveling, connect to local help fast' },
    ],
  },
  {
    title: 'For Government & Law Enforcement',
    icon: Building2,
    color: 'blue',
    features: [
      { name: 'Mass Alert Tools', desc: 'Push health notices, disaster warnings, or crime alerts in real time' },
      { name: 'Citizen Reporting Dashboard', desc: 'Access user-submitted reports, incident data, and heatmaps' },
      { name: 'Integrated Call Centers', desc: 'Handle high-volume incident reports with national access points' },
    ],
  },
  {
    title: 'For Landlords & Householders',
    icon: Home,
    color: 'green',
    features: [
      { name: 'Rescue Me Service', desc: 'On-demand response from nearby mobile patrols for break-ins or threats' },
      { name: 'Visible Deterrence', desc: 'Reduce risk with active guards patrolling your neighborhood' },
      { name: 'Household Access', desc: 'Everyone under your roof can get help with one account' },
    ],
  },
  {
    title: 'For Students',
    icon: GraduationCap,
    color: 'purple',
    features: [
      { name: 'Escort Me Service', desc: 'Live patrol escorts near campus, especially at night' },
      { name: 'One Tap Safety', desc: 'One tap in the app — someone\'s walking you home' },
      { name: 'Campus Zone Coverage', desc: 'Guards stationed near housing and high-risk footpaths' },
    ],
  },
  {
    title: 'For Security Companies',
    icon: Shield,
    color: 'orange',
    features: [
      { name: 'Responder App', desc: 'Equip guards with real-time tracking and dispatch commands' },
      { name: 'Incident Management', desc: 'Get data on calls, alerts, and location history' },
      { name: 'Public-Private Integration', desc: 'Work with public alerts and law enforcement infrastructure' },
    ],
  },
];

const howItWorks = [
  { step: '01', title: 'Alarm Triggered', desc: 'Monitoring companies are notified when an alarm is activated', icon: Bell },
  { step: '02', title: 'Alarm Dispatched', desc: 'Licensed guards receive the alarm alert instantly', icon: Radio },
  { step: '03', title: 'Responder Accepts', desc: 'Nearest responder accepts, progress tracked in real-time', icon: UserCheck },
  { step: '04', title: 'Incident Report', desc: 'Guard submits detailed report with photos and timestamps', icon: FileText },
  { step: '05', title: 'Site Secured', desc: 'Within minutes, the site is secured and peace restored', icon: Shield },
  { step: '06', title: 'Continuous Monitoring', desc: 'Ongoing safety and quick responses to future alarms', icon: Eye },
];

const keyFeatures = [
  { icon: Siren, title: 'One Tap for Help', desc: 'Get immediate assistance for accidents, crime, fire, or medical emergencies' },
  { icon: Bell, title: 'Real-Time Public Alerts', desc: 'Stay informed with trusted updates from national agencies including ZNPHI' },
  { icon: MapPin, title: 'Locate Health Facilities', desc: 'Quickly find the nearest clinic, pharmacy, or hospital when it matters most' },
  { icon: Footprints, title: 'Escort Me Service', desc: 'Live patrol escorts near campus, especially at night for students' },
  { icon: Map, title: 'Incident Heatmaps', desc: 'View user-submitted reports and incident data visualized on maps' },
  { icon: HandHelping, title: 'Community Safety', desc: 'Look out for family, friends and neighbors - build a safer world together' },
];

const SwiftGuardPage = () => {
  return (
    <div className="swiftguard-page" id="swiftguard-content">
      <Header />
      
      {/* Hero Section */}
      <section className="swiftguard-hero">
        <div className="container-custom">
          <div className="swiftguard-hero-content">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="swiftguard-badge"
            >
              <Shield size={16} />
              Your Safety. Your Health. Your Community.
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Swift<span className="text-gradient-red">Guard</span> Responder
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="swiftguard-subtitle"
            >
              From emergency response to health alerts, SwiftGuard connects you to real-time support. 
              Whether you're a student walking home late, a household in need of protection, or a citizen 
              seeking public health updates — SwiftGuard is your lifeline.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="swiftguard-hero-buttons"
            >
              <a href="https://play.google.com/store/apps/details?id=com.swiftguard.app" target="_blank" rel="noopener noreferrer" className="btn-swift-primary">
                Download App <ArrowRight size={16} />
              </a>
              <a href="#features" className="btn-swift-secondary">
                Learn More
              </a>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="hero-highlights-swift"
            >
              <div className="highlight-item-swift">
                <CheckCircle size={20} />
                <span>One Tap SOS</span>
              </div>
              <div className="highlight-item-swift">
                <CheckCircle size={20} />
                <span>Real-Time Alerts</span>
              </div>
              <div className="highlight-item-swift">
                <CheckCircle size={20} />
                <span>24/7 Protection</span>
              </div>
            </motion.div>
          </div>
        </div>
        
        <div className="swiftguard-hero-bg">
          <div className="swift-blob-1" />
          <div className="swift-blob-2" />
        </div>
      </section>
      
      {/* Key Features */}
      <section className="swiftguard-features" id="features">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-swift"
          >
            <span className="section-tag-swift">Key Features</span>
            <h2>What Can SwiftGuard Do For You?</h2>
            <p>Safety should never be a concern. We leverage advanced technology for personal safety management.</p>
          </motion.div>
          
          <div className="key-features-grid">
            {keyFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="key-feature-card"
              >
                <div className="key-feature-icon">
                  <feature.icon size={28} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* User Categories */}
      <section className="swiftguard-users">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-swift"
          >
            <span className="section-tag-swift">For Everyone</span>
            <h2>Features for Every User</h2>
            <p>Tailored solutions for individuals, families, students, businesses, and government</p>
          </motion.div>
          
          <div className="user-categories-grid">
            {userCategories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`user-category-card ${category.color}`}
              >
                <div className="category-header-swift">
                  <div className="category-icon-swift">
                    <category.icon size={24} />
                  </div>
                  <h3>{category.title}</h3>
                </div>
                <ul className="category-features-swift">
                  {category.features.map((feature, i) => (
                    <li key={i}>
                      <Check size={16} />
                      <div>
                        <strong>{feature.name}</strong>
                        <span>{feature.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="swiftguard-how">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-swift light"
          >
            <span className="section-tag-swift light">How It Works</span>
            <h2>SwiftGuard Responder Process</h2>
            <p>From alarm to secured site in minutes</p>
          </motion.div>
          
          <div className="how-it-works-grid">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="how-step-card"
              >
                <div className="step-icon-swift">
                  <step.icon size={24} />
                </div>
                <span className="step-number-swift">{step.step}</span>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Tagline Section */}
      <section className="swiftguard-tagline">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="tagline-content"
          >
            <Clock size={48} />
            <h2>Because Every Second Counts In Safety</h2>
            <p>
              We are committed to providing a platform where you can use your phone to look out for 
              people you care about — family, friends, and neighbors. Prevent incidents and rely on 
              one another. Together, we can build a safer world.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="swiftguard-cta" id="contact">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="cta-content-swift"
          >
            <h2>Ready to Stay Protected?</h2>
            <p>Download SwiftGuard today and join thousands keeping their communities safe.</p>
            <div className="cta-buttons-swift">
              <a href="https://swiftguard.app/" target="_blank" rel="noopener noreferrer" className="btn-swift-primary btn-white">
                <Globe size={18} /> Visit Website
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.swiftguard.app" target="_blank" rel="noopener noreferrer" className="btn-swift-secondary btn-outline-white">
                <Smartphone size={18} /> Get on Google Play
              </a>
            </div>
            <div className="social-links-swift">
              <a href="https://www.facebook.com/swiftguard.responder" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="https://www.instagram.com/swiftguard_responder.app/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <Instagram size={20} />
              </a>
              <a href="https://www.tiktok.com/@swiftguard_responder.app" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a href="https://www.linkedin.com/showcase/swiftguard-responder/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <Linkedin size={20} />
              </a>
            </div>
            <p className="contact-info-swift">
              <MapPin size={16} /> Africa &nbsp;|&nbsp; <Phone size={16} /> +260972119978
            </p>
          </motion.div>
        </div>
      </section>
      
      <Footer />
      <PDFDownloadButton 
        contentId="swiftguard-content" 
        fileName="VoxIT-SwiftGuard-Responder" 
        buttonText="Download PDF"
      />
    </div>
  );
};

export default SwiftGuardPage;
